import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, UserPlus, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';
import { supabase } from '../../lib/supabase';
import type { AutomationStatus } from './types';

const ENROLL_SUBSCRIBER_LIST_LIMIT = 10;

interface SubscriberOption {
  id: string;
  email: string | null;
  name: string | null;
  unsubscribed_at: string | null;
}

interface EnrollAutomationModalProps {
  isOpen: boolean;
  onClose: () => void;
  automationId: string;
  automationName: string;
  automationStatus: AutomationStatus;
  /** Subscriber ids with an active enrollment in this automation — hidden from the list. */
  excludeSubscriberIds: string[];
  onSuccess: () => void;
}

function displayLabel(s: SubscriberOption): string {
  return s.email?.trim() || s.name?.trim() || `subscriber ${s.id}`;
}

export default function EnrollAutomationModal({
  isOpen,
  onClose,
  automationId,
  automationName,
  automationStatus,
  excludeSubscriberIds,
  onSuccess,
}: EnrollAutomationModalProps) {
  const [subscribers, setSubscribers] = useState<SubscriberOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState(false);

  const canEnroll = automationStatus === 'active';

  const loadSubscribers = useCallback(async (query: string) => {
    setLoading(true);
    try {
      let req = supabase
        .from('subscribers')
        .select('id, email, name, unsubscribed_at')
        .is('unsubscribed_at', null)
        .order('email', { ascending: true })
        .limit(ENROLL_SUBSCRIBER_LIST_LIMIT);

      const q = query.trim();
      if (q.length >= 2) {
        const pattern = `%${q.replace(/%/g, '\\%')}%`;
        req = req.or(`email.ilike.${pattern},name.ilike.${pattern}`);
      }

      const { data, error } = await req;
      if (error) throw error;
      setSubscribers((data ?? []) as SubscriberOption[]);
    } catch (e: unknown) {
      reportUiError('subscribers', 'list', e, { context: 'enroll_automation' });
      toast.error(e instanceof Error ? e.message : 'Failed to load subscribers');
      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId(null);
    setSearchQuery('');
    void loadSubscribers('');
  }, [isOpen, loadSubscribers]);

  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => {
      void loadSubscribers(searchQuery);
    }, 300);
    return () => window.clearTimeout(t);
  }, [isOpen, searchQuery, loadSubscribers]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !enrolling) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, enrolling]);

  const excluded = useMemo(() => new Set(excludeSubscriberIds), [excludeSubscriberIds]);

  const visible = useMemo(
    () => subscribers.filter((s) => !excluded.has(s.id)).slice(0, ENROLL_SUBSCRIBER_LIST_LIMIT),
    [subscribers, excluded],
  );

  const listCapped =
    !loading &&
    searchQuery.trim().length < 2 &&
    subscribers.length >= ENROLL_SUBSCRIBER_LIST_LIMIT;

  const handleEnroll = async () => {
    if (!canEnroll) {
      toast.error('Automation must be active before enrolling subscribers');
      return;
    }
    if (!selectedId) {
      toast.error('Pick a subscriber first');
      return;
    }
    setEnrolling(true);
    try {
      const { error } = await supabase.rpc('enroll_subscriber_in_automation', {
        p_subscriber_id: selectedId,
        p_automation_id: automationId,
      });
      if (error) throw error;
      toast.success('Subscriber enrolled');
      onSuccess();
      onClose();
    } catch (e: unknown) {
      reportUiError('automation_enrollments', 'enroll', e, {
        automation_id: automationId,
        subscriber_id: selectedId,
      });
      const raw = e instanceof Error ? e.message : 'Enrollment failed';
      const friendly =
        raw.includes('automation_not_active')
          ? 'This automation is not active. Set status to active first.'
          : raw.includes('subscriber_already_enrolled')
            ? 'Subscriber is already actively enrolled in this automation.'
            : raw.includes('automation_not_found')
              ? 'Automation no longer exists.'
              : raw;
      toast.error(friendly);
    } finally {
      setEnrolling(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={() => {
        if (!enrolling) onClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="enroll-automation-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2
            id="enroll-automation-title"
            className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"
          >
            <UserPlus className="w-5 h-5 text-emerald-500" />
            Enroll subscriber
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={enrolling}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700/80">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Add a subscriber to <span className="font-medium text-gray-900 dark:text-white">{automationName}</span>.
            They receive scheduled sends for each active step.
          </p>
          {!canEnroll ? (
            <p className="mt-2 text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2">
              This automation is <span className="font-medium">{automationStatus}</span>. Set status to{' '}
              <span className="font-medium">active</span> on the Overview tab before enrolling.
            </p>
          ) : null}
        </div>

        <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700/80">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by email or name…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              autoFocus
            />
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Need a new contact?{' '}
            <Link to="/subscribers" className="text-emerald-600 dark:text-emerald-400 hover:underline">
              Open Subscribers
            </Link>{' '}
            to create one, then return here.
            {listCapped ? (
              <>
                {' '}
                Showing the first {ENROLL_SUBSCRIBER_LIST_LIMIT} — search to find others.
              </>
            ) : null}
          </p>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : visible.length === 0 ? (
            <p className="py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
              {searchQuery.trim().length >= 2
                ? 'No matching subscribers found.'
                : excluded.size > 0 && subscribers.length > 0
                  ? 'All matching subscribers are already enrolled in this automation.'
                  : 'No subscribers yet. Create one from the Subscribers page.'}
            </p>
          ) : (
            <ul className="space-y-2">
              {visible.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(s.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedId === s.id
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/30'
                    }`}
                  >
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {displayLabel(s)}
                    </div>
                    {s.name && s.email ? (
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{s.name}</div>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={enrolling}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleEnroll()}
            disabled={enrolling || !selectedId || !canEnroll}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
          >
            {enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
            {enrolling ? 'Enrolling…' : 'Enroll subscriber'}
          </button>
        </div>
      </div>
    </div>
  );
}
